/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx, css } from '@emotion/react'
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react'
import { debounce } from 'lodash'
import { TextField, TextFieldProps, Typography } from '@mui/material'
import { nanoid } from 'nanoid'

export type Props = {
  value: string | null
  charLimit?: number
  hideCharCounter?: boolean
  enableDebouncedOnChange?: boolean
  changeValidator?: (value: string) => boolean
} & Omit<TextFieldProps, 'id'>

export default function InputGroup(props: Props): JSX.Element {
  const {
    changeValidator,
    charLimit,
    className,
    hideCharCounter,
    enableDebouncedOnChange = true,
    multiline,
    error,
    label,
    name,
    onChange,
    placeholder,
    helperText,
    variant = 'outlined' as any,
    style,
    value: propsValue,
    size = 'small',
    ...restOfProps
  } = props
  const id = useRef(nanoid())

  const [value, setValue] = useState<string>('')

  useEffect(() => {
    if (propsValue !== value) {
      setValue(propsValue || '')
    }
  }, [propsValue])

  const debouncedOnChangeHandler = useCallback(
    debounce((evt: any) => {
      if (onChange) {
        onChange(evt)
      }
    }, 300),
    []
  )

  function handleOnChange(evt: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const event: any = { target: { name: evt.target.name, value: evt.target.value } }
    if (
      (!charLimit || (charLimit && evt.target.value.length <= charLimit)) &&
      (!changeValidator || changeValidator(evt.target.value))
    ) {
      setValue(evt.target.value)

      if (onChange) {
        if (enableDebouncedOnChange) {
          debouncedOnChangeHandler(event)
        } else {
          onChange(event)
        }
      }
    }
  }

  return (
    <div className={className} style={style}>
      <TextField
        id={id.current}
        error={error}
        label={label}
        name={name}
        onChange={handleOnChange}
        placeholder={placeholder}
        value={value}
        variant={variant}
        multiline={multiline}
        size={size}
        {...restOfProps}
      />
      <div>
        {helperText && (
          <Typography
            css={css`
              float: left;
              margin-left: 14px;
              margin-top: 4px;
              color: ${error ? 'red' : 'rgba(0, 0, 0, 0.54)'};
              width: calc(100% - 82px);
            `}
            variant="caption"
          >
            {helperText}
          </Typography>
        )}
        {charLimit !== undefined && !hideCharCounter && (
          <Typography
            css={css`
              font-size: 0.75rem;
              text-align: right;
              float: right;
              margin-left: 14px;
              margin-right: 14px;
              margin-top: 4px;
              color: ${error ? 'red' : 'rgba(0, 0, 0, 0.54)'};
              width: 40px;
            `}
            variant="subtitle1"
          >
            {value.length}/{charLimit}
          </Typography>
        )}
      </div>
    </div>
  )
}
